export const socialPsychologyCourse = {
  id: "social-psychology-2024",
  title: "Social Psychology: Understanding Human Interaction & Behavior",
  shortDescription: "Explore how social context influences thoughts, feelings, and behaviors in interpersonal and group settings.",
  description: "Comprehensive study of social psychology covering attitudes, conformity, group dynamics, prejudice, aggression, and prosocial behavior. Students will examine how individuals are influenced by social situations and how they influence others in return.",
  category: "Social Sciences",
  industryCategory: "Social Sciences",
  difficulty: "intermediate",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from social psychology professors and behavioral researchers",
  thumbnailUrl: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand how social situations influence individual behavior",
    "Analyze the formation and change of attitudes and beliefs",
    "Examine conformity, compliance, and obedience phenomena",
    "Evaluate group dynamics and decision-making processes",
    "Understand the psychology of prejudice and discrimination",
    "Analyze factors influencing aggression and prosocial behavior",
    "Examine interpersonal attraction and relationship formation",
    "Apply social psychological principles to real-world situations"
  ],

  prerequisites: [
    "Introduction to Psychology or equivalent",
    "Basic understanding of research methods",
    "Interest in human social behavior"
  ],

  skills: [
    "Social Analysis",
    "Behavioral Observation",
    "Research Design",
    "Statistical Analysis",
    "Critical Thinking",
    "Interpersonal Skills",
    "Cultural Awareness",
    "Communication Skills"
  ],

  marketData: {
    averageSalary: 60000,
    salaryRange: {
      min: 42000,
      max: 85000
    },
    jobDemand: 4.0,
    marketTrend: "growing",
    careerPaths: [
      "Social Psychologist",
      "Market Research Analyst",
      "Human Resources Specialist",
      "Organizational Consultant",
      "Consumer Behavior Analyst",
      "Social Researcher"
    ],
    relatedSkills: [
      "Social Psychology",
      "Research Methods",
      "Data Analysis",
      "Survey Design",
      "Group Facilitation",
      "Behavior Analysis"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "social-influence",
      title: "Social Influence and Conformity",
      description: "How individuals are influenced by others through conformity, compliance, and obedience",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "social-influence-intro",
          title: "Introduction to Social Influence",
          description: "Overview of how people influence each other in social situations",
          type: "video",
          duration: 30
        },
        {
          id: "conformity-compliance",
          title: "Conformity and Compliance",
          description: "Classic studies and theories of social influence",
          type: "text",
          duration: 45,
          textContent: `# Conformity and Compliance

## The Power of Social Influence

### Understanding Social Influence

Social influence occurs when individuals change their thoughts, feelings, or behaviors due to the real or imagined presence of others.

**Types of Social Influence:**
- **Conformity**: Changing behavior to match group norms
- **Compliance**: Agreeing to requests from others
- **Obedience**: Following direct orders from authority figures

### Conformity

**Definition**
Conformity is the tendency to change one's beliefs or behaviors to match those of others in a group.

**Asch's Line Studies (1951)**
Solomon Asch conducted famous experiments showing that people will conform to obviously incorrect group judgments.

**Key Findings:**
- About 75% of participants conformed at least once
- Average conformity rate was about 33%
- When one other person gave correct answer, conformity dropped dramatically
- Conformity increased with group size (up to 3-4 people)

**Types of Conformity**

**Informational Social Influence**
- Conforming because we believe others have correct information
- Occurs in ambiguous situations
- Leads to private acceptance of group position
- **Example**: Following crowd during emergency evacuation

**Normative Social Influence**
- Conforming to be liked and accepted by group
- Occurs even when we disagree privately
- Leads to public compliance without private acceptance
- **Example**: Dressing according to fashion trends

### Factors Affecting Conformity

**Group Factors**
- **Group Size**: Conformity increases up to 3-4 people, then levels off
- **Unanimity**: Even one dissenter dramatically reduces conformity
- **Group Cohesion**: More cohesive groups produce greater conformity
- **Status**: Higher status groups create more pressure to conform

**Individual Factors**
- **Self-Esteem**: Lower self-esteem associated with higher conformity
- **Cultural Background**: Collectivist cultures show more conformity
- **Gender**: Women may conform more in male-dominated domains
- **Personality**: Need for uniqueness reduces conformity

**Situational Factors**
- **Ambiguity**: More ambiguous situations increase conformity
- **Difficulty**: Harder tasks increase reliance on others
- **Public vs. Private**: Public responses show more conformity

### Compliance

**Definition**
Compliance occurs when people agree to requests from others, even without changing their private beliefs.

**Foot-in-the-Door Technique**
- Start with small request, then make larger request
- **Mechanism**: Consistency principle - people want to appear consistent
- **Example**: First ask to sign petition, then ask for donation

**Door-in-the-Face Technique**
- Start with large request (expect rejection), then smaller request
- **Mechanism**: Reciprocal concessions - feel obligated to make concession
- **Example**: First ask for $100 donation, then ask for $10

**Low-Ball Technique**
- Get initial commitment, then reveal additional costs
- **Mechanism**: Commitment and consistency
- **Example**: Agree to buy car, then told about additional fees

**That's-Not-All Technique**
- Make offer, then improve it before response
- **Mechanism**: Reciprocity - feel obligated for "favor"
- **Example**: "But wait, there's more!" advertising

### Obedience to Authority

**Milgram's Obedience Studies (1963)**
Stanley Milgram studied obedience to authority by having participants deliver electric shocks to learners.

**Key Findings:**
- 65% of participants delivered maximum shock (450 volts)
- Most participants showed extreme stress but continued
- Obedience decreased when authority figure was absent
- Obedience decreased when victim was more visible

**Factors Influencing Obedience**
- **Proximity of Authority**: Closer authority increases obedience
- **Legitimacy of Authority**: Recognized institutions increase obedience
- **Proximity of Victim**: Closer victim decreases obedience
- **Personal Responsibility**: Diffused responsibility increases obedience

### Real-World Applications

**Marketing and Sales**
- **Commitment Techniques**: Getting public commitments
- **Social Proof**: Showing others' behavior
- **Authority Appeals**: Using expert endorsements
- **Scarcity**: Creating urgency through limited availability

**Social Movements**
- **Foot-in-the-Door**: Starting with small actions
- **Social Norms**: Changing perceptions of what's normal
- **Group Identity**: Creating sense of belonging
- **Authority Support**: Getting leader endorsements

**Workplace Behavior**
- **Team Cohesion**: Creating conformity pressure
- **Dress Codes**: Normative influence on appearance
- **Meeting Dynamics**: Conformity in group decisions
- **Hierarchy**: Obedience to management authority

### Resistance to Social Influence

**Strategies for Resistance**
- **Awareness**: Understanding influence techniques
- **Values Clarification**: Knowing personal values
- **Social Support**: Having allies who share views
- **Assertiveness Training**: Learning to say no

**Individual Differences**
- **Reactance**: Some people resist when freedom threatened
- **Need for Uniqueness**: Desire to be different from others
- **Cognitive Resources**: Ability to think critically about requests
- **Self-Confidence**: Belief in own judgment

Understanding social influence helps us recognize when we're being influenced and make more conscious choices about when to conform and when to resist.`
        },
        {
          id: "influence-scenarios",
          title: "Social Influence Scenarios",
          description: "Analyze different types of social influence in real-world situations",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "influence-analysis",
            instructions: "Identify the type of social influence operating in each scenario and analyze the psychological mechanisms involved.",
            scenarios: [
              {
                id: "workplace-meeting",
                scenario: "In a team meeting, everyone agrees with the boss's proposal even though some privately disagree",
                influenceType: "Normative conformity",
                mechanisms: ["Authority pressure", "Fear of rejection", "Public compliance"],
                factors: ["Power dynamics", "Group cohesion", "Fear of consequences"]
              },
              {
                id: "charity-donation",
                scenario: "A caller asks for a $500 donation, you refuse, then they ask for $50 and you agree",
                influenceType: "Door-in-the-face technique",
                mechanisms: ["Reciprocal concessions", "Contrast effect", "Compliance"],
                factors: ["Size of initial request", "Timing", "Perceived reasonableness"]
              }
            ],
            hints: [
              "Consider whether influence is public or private",
              "Think about the motivation behind the behavior change",
              "Look for specific compliance techniques being used"
            ]
          }
        }
      ],
      quiz: {
        id: "social-influence-quiz",
        title: "Social Influence Quiz",
        description: "Test your understanding of conformity, compliance, and obedience",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In Asch's conformity studies, what happened when one confederate gave the correct answer?",
            options: [
              "Conformity increased dramatically",
              "Conformity decreased dramatically",
              "Conformity stayed the same",
              "Participants became confused"
            ],
            correctAnswer: 1,
            explanation: "When just one confederate broke the unanimity by giving the correct answer, conformity dropped dramatically, showing the power of having an ally."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "The foot-in-the-door technique works because of:",
            options: [
              "Reciprocity principle",
              "Authority influence",
              "Consistency principle",
              "Social proof"
            ],
            correctAnswer: 2,
            explanation: "The foot-in-the-door technique works because people want to appear consistent - after agreeing to a small request, they feel pressure to agree to larger requests to maintain consistency."
          }
        ]
      }
    },
    {
      id: "attitudes-persuasion",
      title: "Attitudes and Persuasion",
      description: "Formation, change, and measurement of attitudes; theories of persuasion and attitude change",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "attitudes-intro",
          title: "Nature of Attitudes",
          description: "What are attitudes and how do they influence behavior?",
          type: "video",
          duration: 35
        },
        {
          id: "attitude-formation",
          title: "Attitude Formation and Change",
          description: "Comprehensive study of how attitudes develop and change over time",
          type: "text",
          duration: 45,
          textContent: `# Attitude Formation and Change

## Understanding Attitudes

### Definition and Components

**What are Attitudes?**
Attitudes are relatively enduring evaluations of people, objects, issues, or concepts that influence our thoughts, feelings, and behaviors.

**Tripartite Model of Attitudes**

**Cognitive Component**
- **Beliefs and thoughts** about the attitude object
- **Knowledge and information** we possess
- **Stereotypes and expectations**
- **Example**: "Electric cars are environmentally friendly"

**Affective Component**
- **Emotional reactions** and feelings
- **Positive or negative evaluations**
- **Gut reactions and preferences**
- **Example**: "I feel good about driving an electric car"

**Behavioral Component**
- **Actions and behavioral intentions**
- **Past behaviors** toward the object
- **Planned future behaviors**
- **Example**: "I intend to buy an electric car"

### Attitude Formation

**Direct Experience**
- **Personal contact** with attitude object
- **Behavioral experiences** and their consequences
- **Emotional experiences** associated with object
- **Stronger predictor** of future behavior than indirect experience

**Social Learning**
- **Observational learning** from others' experiences
- **Vicarious conditioning** through others' reactions
- **Modeling** of significant others' attitudes
- **Social reinforcement** for expressing certain attitudes

**Classical Conditioning**
- **Pairing** attitude object with positive/negative stimuli
- **Evaluative conditioning** - automatic preference formation
- **Mere exposure effect** - familiarity breeds liking
- **Example**: Products paired with attractive models or pleasant music

**Instrumental Conditioning**
- **Reinforcement** for expressing certain attitudes
- **Punishment** for expressing opposing views
- **Social approval/disapproval** as consequences
- **Shapes public expression** of attitudes

**Cognitive Processes**
- **Information processing** and evaluation
- **Logical reasoning** about attitude object
- **Belief formation** through evidence evaluation
- **Cognitive consistency** needs

### Attitude Measurement

**Self-Report Measures**

**Likert Scales**
- **Format**: Agree/disagree with statements
- **Range**: Usually 5 or 7 points
- **Advantages**: Easy to construct and administer
- **Limitations**: Social desirability bias

**Semantic Differential**
- **Format**: Rate on bipolar adjective scales
- **Example**: Good-Bad, Strong-Weak, Active-Passive
- **Advantages**: Measures attitude dimensions
- **Applications**: Brand image, concept evaluation

**Implicit Measures**

**Implicit Association Test (IAT)**
- **Method**: Measures automatic associations
- **Speed** of categorization reveals implicit attitudes
- **Less susceptible** to social desirability
- **Applications**: Prejudice, consumer preferences

**Physiological Measures**
- **Facial EMG**: Muscle activity indicating emotion
- **Skin conductance**: Arousal responses
- **fMRI**: Brain activation patterns
- **Eye tracking**: Visual attention patterns

## Theories of Attitude Change

### Cognitive Dissonance Theory

**Leon Festinger (1957)**
When cognitions are inconsistent, we experience dissonance (psychological discomfort) and are motivated to reduce it.

**Sources of Dissonance**
- **Attitude-behavior inconsistency**
- **Post-decision dissonance** after choosing between alternatives
- **Effort justification** - valuing things we work hard for
- **Insufficient justification** - changing attitudes when external rewards are small

**Dissonance Reduction Strategies**
- **Change attitude** to match behavior
- **Change behavior** to match attitude
- **Add consonant cognitions** (new supporting beliefs)
- **Reduce importance** of inconsistent cognitions

**Applications**
- **Initiation rituals** create commitment through effort justification
- **Foot-in-the-door** technique uses consistency pressure
- **Choice architecture** can influence post-decision attitudes

### Elaboration Likelihood Model

**Petty and Cacioppo (1986)**
Two routes to persuasion based on motivation and ability to process information.

**Central Route**
- **High elaboration**: Careful processing of message content
- **Conditions**: High motivation, high ability
- **Focus**: Quality of arguments and evidence
- **Results**: More enduring attitude change
- **Resistance**: Higher resistance to counter-persuasion

**Peripheral Route**
- **Low elaboration**: Reliance on simple cues
- **Conditions**: Low motivation or low ability
- **Focus**: Source attractiveness, number of arguments, emotions
- **Results**: Temporary attitude change
- **Resistance**: Lower resistance to counter-persuasion

**Factors Affecting Route**

**Motivation Factors**
- **Personal relevance**: How much does issue matter to you?
- **Need for cognition**: Individual difference in enjoying thinking
- **Personal responsibility**: Will you be held accountable?

**Ability Factors**
- **Knowledge**: Do you understand the topic?
- **Intelligence**: Can you process complex arguments?
- **Distraction**: Are there competing demands on attention?
- **Time pressure**: Do you have time to think carefully?

### Social Judgment Theory

**Muzafer Sherif**
Attitude change depends on how far the persuasive message is from the receiver's current position.

**Latitude of Acceptance**
- **Range of positions** the person finds acceptable
- **Messages within this range** may be persuasive
- **Assimilation effect**: Messages pulled closer to own position

**Latitude of Rejection**
- **Range of positions** the person finds unacceptable
- **Messages in this range** typically rejected
- **Contrast effect**: Messages pushed further from own position

**Latitude of Non-commitment**
- **Neutral range** where person has no strong opinion
- **Greatest potential** for attitude change
- **Messages here** processed more objectively

## Persuasion Strategies

### Source Characteristics

**Credibility**
- **Expertise**: Perceived knowledge and competence
- **Trustworthiness**: Perceived honesty and lack of bias
- **Sleeper effect**: Low-credibility sources can become persuasive over time

**Attractiveness**
- **Physical attractiveness**: Halo effect influences persuasion
- **Similarity**: We're influenced by people like us
- **Liking**: We agree with people we like

**Power**
- **Legitimate authority**: Official position or role
- **Expert power**: Superior knowledge or skill
- **Referent power**: Identification with the source

### Message Characteristics

**Argument Quality**
- **Strong arguments**: Logical, evidence-based, compelling
- **Weak arguments**: Illogical, unsupported, irrelevant
- **Matters most** when people are motivated and able to process

**Message Structure**
- **One-sided vs. two-sided**: Acknowledge counterarguments?
- **Primacy vs. recency**: Which arguments come first?
- **Explicit vs. implicit conclusions**: State conclusion or let audience infer?

**Emotional Appeals**
- **Fear appeals**: Threat + efficacy = attitude change
- **Positive emotions**: Create good feelings associated with position
- **Guilt appeals**: Make people feel responsible for problems

### Audience Characteristics

**Individual Differences**
- **Need for cognition**: Enjoy thinking and analyzing
- **Self-monitoring**: Attention to social cues
- **Intelligence**: Ability to process complex messages
- **Self-esteem**: Confidence in own judgments

**Demographic Factors**
- **Age**: Younger people more susceptible to change
- **Education**: Higher education increases critical thinking
- **Gender**: Different responses to different appeals
- **Culture**: Collectivist vs. individualist orientations

## Resistance to Persuasion

### Cognitive Defenses

**Inoculation Theory**
- **William McGuire**: Like medical inoculation
- **Weak attacks** on attitudes build resistance
- **Practice defending** attitudes strengthens them
- **Applications**: Counter-propaganda, education

**Reactance Theory**
- **Jack Brehm**: Threats to freedom motivate resistance
- **Psychological reactance**: Motivation to restore freedom
- **Boomerang effect**: Persuasion attempts backfire
- **Importance of choice** and perceived freedom

### Motivated Reasoning

**Biased Processing**
- **Confirmation bias**: Seek supporting information
- **Disconfirmation bias**: Critically evaluate opposing information
- **Source derogation**: Attack credibility of opposing sources
- **Counterarguing**: Generate reasons to reject message

**Selective Exposure**
- **Choose supportive information** sources
- **Avoid challenging information**
- **Echo chambers** and filter bubbles
- **Polarization** through selective exposure

## Real-World Applications

### Marketing and Advertising
- **Brand attitudes**: Creating positive associations
- **Consumer behavior**: Attitude-behavior relationships
- **Celebrity endorsements**: Source attractiveness effects
- **Emotional advertising**: Peripheral route persuasion

### Health Communication
- **Behavior change campaigns**: Quit smoking, exercise
- **Fear appeals**: Health risks and prevention
- **Social norms**: What others are doing
- **Self-efficacy**: Confidence in ability to change

### Political Communication
- **Candidate image**: Source characteristics
- **Issue framing**: How problems are presented
- **Negative advertising**: Attack opponent credibility
- **Political polarization**: Attitude extremity and resistance

Understanding attitudes and persuasion helps us become more effective communicators and more discerning consumers of persuasive messages.`
        },
        {
          id: "persuasion-analysis",
          title: "Persuasion Technique Analysis",
          description: "Analyze real-world persuasion attempts using psychological theories",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "persuasion-analysis",
            instructions: "Analyze advertisements and political messages for persuasion techniques and psychological principles.",
            examples: [
              {
                id: "car-ad",
                type: "Advertisement",
                description: "Luxury car ad featuring celebrity endorsement and emotional music",
                techniques: ["Celebrity endorsement (source attractiveness)", "Emotional appeal (peripheral route)", "Status association"],
                route: "Peripheral",
                effectiveness: "High for low-involvement consumers"
              },
              {
                id: "health-campaign",
                type: "Public Service Announcement",
                description: "Anti-smoking campaign with graphic health warnings and statistics",
                techniques: ["Fear appeal", "Expert testimony", "Statistical evidence (central route)"],
                route: "Central",
                effectiveness: "High for motivated, health-conscious individuals"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "attitudes-persuasion-quiz",
        title: "Attitudes and Persuasion Quiz",
        description: "Test your understanding of attitude formation, change, and persuasion theories",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to the Elaboration Likelihood Model, the central route to persuasion is characterized by:",
            options: [
              "Reliance on simple cues like source attractiveness",
              "Careful processing of message content and arguments",
              "Automatic attitude change without thinking",
              "Focus on peripheral factors like music and imagery"
            ],
            correctAnswer: 1,
            explanation: "The central route involves careful, systematic processing of the actual arguments and evidence in a persuasive message."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Cognitive dissonance is most likely to occur when:",
            options: [
              "Our attitudes and behaviors are consistent",
              "We hold conflicting beliefs or our behavior contradicts our attitudes",
              "We have no opinion on an issue",
              "We agree with everyone around us"
            ],
            correctAnswer: 1,
            explanation: "Cognitive dissonance occurs when we hold conflicting cognitions or when our behavior contradicts our attitudes, creating psychological discomfort."
          }
        ]
      }
    },
    {
      id: "group-dynamics",
      title: "Group Dynamics and Team Behavior",
      description: "How groups form, function, and influence individual behavior and decision-making",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "group-formation",
          title: "Group Formation and Development",
          description: "How groups form and evolve through different stages",
          type: "video",
          duration: 30
        },
        {
          id: "group-processes",
          title: "Group Processes and Performance",
          description: "Comprehensive examination of how groups function and perform",
          type: "text",
          duration: 50,
          textContent: `# Group Processes and Performance

## Understanding Groups

### Definition and Types of Groups

**What is a Group?**
A group is two or more people who interact with each other, share common goals, and perceive themselves as belonging together.

**Characteristics of Groups**
- **Interaction**: Members communicate and influence each other
- **Structure**: Roles, norms, and status hierarchies
- **Cohesion**: Attraction and commitment to the group
- **Goals**: Shared purposes and objectives
- **Identity**: Sense of "we-ness" and belonging

**Types of Groups**

**Primary vs. Secondary Groups**
- **Primary**: Close, personal relationships (family, close friends)
- **Secondary**: More formal, task-oriented relationships (work teams, committees)

**Formal vs. Informal Groups**
- **Formal**: Officially established with clear structure (departments, committees)
- **Informal**: Emerge naturally based on personal relationships (lunch groups, friendship circles)

**In-groups vs. Out-groups**
- **In-groups**: Groups we belong to and identify with
- **Out-groups**: Groups we don't belong to or identify against
- **Creates bias**: Favoritism toward in-group members

### Group Formation

**Tuckman's Model of Group Development**

**Forming Stage**
- **Characteristics**: Uncertainty, politeness, testing boundaries
- **Tasks**: Getting acquainted, establishing ground rules
- **Leadership**: Often directive, providing structure
- **Duration**: Can be brief or extended

**Storming Stage**
- **Characteristics**: Conflict, competition, challenging authority
- **Tasks**: Negotiating roles, establishing hierarchy
- **Leadership**: Coaching, conflict management
- **Challenge**: Many groups fail at this stage

**Norming Stage**
- **Characteristics**: Cooperation, consensus, role clarity
- **Tasks**: Establishing procedures, building cohesion
- **Leadership**: Supporting, facilitating
- **Outcome**: Sense of team identity emerges

**Performing Stage**
- **Characteristics**: High productivity, effective collaboration
- **Tasks**: Achieving goals, maintaining relationships
- **Leadership**: Delegating, monitoring
- **Result**: Peak group performance

**Adjourning Stage**
- **Characteristics**: Completion, reflection, separation
- **Tasks**: Wrapping up, celebrating achievements
- **Emotions**: Can include sadness about ending
- **Also called**: "Mourning" stage

### Group Structure

**Roles in Groups**

**Task Roles**
- **Information seeker**: Asks for facts and clarification
- **Information giver**: Provides relevant information
- **Opinion seeker**: Asks for values and opinions
- **Initiator**: Proposes new ideas and solutions
- **Coordinator**: Organizes group activities
- **Evaluator**: Assesses group progress and decisions

**Maintenance Roles**
- **Encourager**: Supports and praises others
- **Harmonizer**: Mediates conflicts and reduces tension
- **Gatekeeper**: Ensures equal participation
- **Standard setter**: Helps establish group norms
- **Follower**: Goes along with group decisions

**Individual Roles (Dysfunctional)**
- **Aggressor**: Attacks others' ideas or persons
- **Blocker**: Opposes all suggestions
- **Recognition seeker**: Draws attention to self
- **Dominator**: Monopolizes discussion
- **Avoider**: Withdraws from group activities

**Group Norms**

**Definition**: Informal rules about acceptable behavior in the group

**Types of Norms**
- **Performance norms**: How hard to work
- **Appearance norms**: How to dress and present oneself
- **Social norms**: How to interact with others
- **Resource allocation norms**: How to distribute rewards

**Norm Development**
- **Explicit statements**: Direct discussion of expectations
- **Critical events**: Responses to significant incidents
- **Primacy**: Early behaviors set precedents
- **Carry-over**: Bring norms from other groups

**Status and Hierarchy**

**Sources of Status**
- **Formal position**: Official rank or title
- **Expertise**: Knowledge and skills
- **Contribution**: Value added to group
- **Personal characteristics**: Charisma, attractiveness
- **External status**: Position outside the group

**Status Effects**
- **Communication patterns**: High-status members talk more
- **Influence**: Higher status leads to greater influence
- **Expectations**: Different standards for different levels
- **Mobility**: Difficulty changing status positions

## Group Performance

### Social Facilitation

**Zajonc's Drive Theory**
The presence of others increases arousal, which enhances performance on simple tasks but impairs performance on complex tasks.

**Simple/Well-learned Tasks**
- **Improvement**: Performance gets better with audience
- **Examples**: Simple math, familiar skills
- **Mechanism**: Arousal energizes dominant responses

**Complex/New Tasks**
- **Impairment**: Performance gets worse with audience
- **Examples**: Learning new skills, creative problem-solving
- **Mechanism**: Arousal interferes with complex processing

**Factors Affecting Social Facilitation**
- **Evaluation apprehension**: Fear of being judged
- **Distraction-conflict**: Competing attention demands
- **Self-awareness**: Increased focus on performance

### Social Loafing

**Definition**: Tendency for individuals to exert less effort in groups than when working alone

**Ringelmann Effect**: Early demonstration with rope-pulling task
- **Individual effort**: 100% when alone
- **Groups of 2**: 93% per person
- **Groups of 8**: 49% per person

**Causes of Social Loafing**
- **Diffusion of responsibility**: Individual contributions less identifiable
- **Reduced accountability**: Lower chance of evaluation
- **Sucker effect**: Avoid being taken advantage of
- **Free riding**: Benefit from others' efforts

**Reducing Social Loafing**
- **Individual accountability**: Make contributions identifiable
- **Task meaningfulness**: Increase personal investment
- **Group cohesion**: Strengthen commitment to group
- **Appropriate group size**: Smaller groups reduce loafing
- **Clear goals**: Specific, challenging objectives

### Group Decision-Making

**Group vs. Individual Decisions**

**Advantages of Group Decisions**
- **More information**: Access to diverse knowledge
- **Multiple perspectives**: Different viewpoints
- **Error detection**: Others can catch mistakes
- **Acceptance**: Participation increases buy-in

**Disadvantages of Group Decisions**
- **Time consuming**: Takes longer than individual decisions
- **Compromise**: May lead to mediocre solutions
- **Conformity pressure**: Reluctance to dissent
- **Process losses**: Coordination and communication costs

**Groupthink**

**Irving Janis**: Faulty decision-making in highly cohesive groups

**Symptoms of Groupthink**
- **Illusion of unanimity**: Everyone seems to agree
- **Self-censorship**: Members withhold dissenting views
- **Pressure on dissenters**: Direct pressure to conform
- **Mindguards**: Members protect group from contrary information
- **Illusion of invulnerability**: Overconfidence in group abilities
- **Belief in group's morality**: Assumption group is always right
- **Stereotyping**: Negative views of outsiders
- **Illusion of unanimity**: Mistaken belief everyone agrees

**Preventing Groupthink**
- **Encourage dissent**: Assign devil's advocate role
- **Seek outside opinions**: Bring in external experts
- **Leader restraint**: Leaders should not state preferences early
- **Subdivide groups**: Use multiple independent groups
- **Second-chance meetings**: Reconsider decisions

**Group Polarization**

**Definition**: Groups make more extreme decisions than individuals would make alone

**Risky Shift**: Early finding that groups make riskier decisions
- **Later research**: Groups can shift toward either extreme
- **Direction**: Depends on initial tendencies of group members

**Explanations**
- **Social comparison**: Want to be above average in valued direction
- **Persuasive arguments**: Hear new reasons supporting initial position
- **Social identity**: Differentiate from other groups

## Team Effectiveness

### Characteristics of Effective Teams

**Clear Goals and Objectives**
- **Specific**: Well-defined outcomes
- **Measurable**: Quantifiable success criteria
- **Achievable**: Realistic given resources
- **Relevant**: Aligned with organizational goals
- **Time-bound**: Clear deadlines

**Appropriate Composition**
- **Size**: Optimal team size (typically 5-9 members)
- **Skills**: Necessary expertise represented
- **Diversity**: Different perspectives and backgrounds
- **Roles**: Clear role definitions and responsibilities

**Effective Processes**
- **Communication**: Open, honest, frequent
- **Decision-making**: Appropriate methods for situation
- **Conflict resolution**: Constructive handling of disagreements
- **Problem-solving**: Systematic approaches to challenges

**Strong Leadership**
- **Vision**: Clear direction and inspiration
- **Support**: Removing obstacles and providing resources
- **Development**: Building team capabilities
- **Recognition**: Acknowledging contributions and achievements

### Team Development Interventions

**Team Building Activities**
- **Trust-building exercises**: Increase interpersonal trust
- **Communication training**: Improve interaction skills
- **Role clarification**: Define expectations and responsibilities
- **Problem-solving training**: Develop systematic approaches

**Process Consultation**
- **Observation**: External consultant observes team processes
- **Feedback**: Providing insights about group dynamics
- **Intervention**: Suggesting improvements
- **Follow-up**: Monitoring progress and adjustments

### Virtual Teams

**Challenges of Virtual Teams**
- **Communication**: Reduced nonverbal cues
- **Trust**: Harder to build relationships
- **Coordination**: Difficult to synchronize activities
- **Social connections**: Less informal interaction

**Success Factors for Virtual Teams**
- **Technology**: Reliable communication tools
- **Protocols**: Clear communication procedures
- **Regular meetings**: Scheduled check-ins
- **Face-to-face meetings**: Periodic in-person gatherings
- **Cultural awareness**: Understanding different backgrounds

Understanding group dynamics helps us work more effectively in teams and make better decisions collectively.`
        },
        {
          id: "team-simulation",
          title: "Team Dynamics Simulation",
          description: "Experience and analyze group processes through interactive scenarios",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "group-dynamics",
            instructions: "Analyze team scenarios and identify group dynamics principles at work.",
            scenarios: [
              {
                id: "team-conflict",
                situation: "A project team is stuck in the storming stage with two members constantly disagreeing",
                dynamics: ["Role conflict", "Status competition", "Norm establishment"],
                interventions: ["Clarify roles", "Establish ground rules", "Facilitate communication"],
                stage: "Storming"
              },
              {
                id: "groupthink-risk",
                situation: "A highly cohesive team always reaches quick consensus and never seems to disagree",
                dynamics: ["Groupthink symptoms", "Pressure for unanimity", "Self-censorship"],
                interventions: ["Assign devil's advocate", "Seek outside input", "Encourage dissent"],
                risk: "Poor decision quality"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "group-dynamics-quiz",
        title: "Group Dynamics Quiz",
        description: "Test your understanding of group formation, processes, and performance",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In Tuckman's model, which stage is characterized by conflict and competition?",
            options: [
              "Forming",
              "Storming",
              "Norming",
              "Performing"
            ],
            correctAnswer: 1,
            explanation: "The storming stage is characterized by conflict, competition, and challenging of authority as group members negotiate roles and establish hierarchy."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Social loafing can be reduced by:",
            options: [
              "Increasing group size",
              "Making individual contributions identifiable",
              "Reducing task difficulty",
              "Eliminating group goals"
            ],
            correctAnswer: 1,
            explanation: "Making individual contributions identifiable increases accountability and reduces the tendency for social loafing in groups."
          }
        ]
      }
    },
    {
      id: "prejudice-stereotyping",
      title: "Prejudice, Stereotyping, and Discrimination",
      description: "Understanding the psychology of prejudice, stereotype formation, and intergroup relations",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "prejudice-intro",
          title: "Introduction to Prejudice and Stereotyping",
          description: "Definitions and basic concepts in the study of prejudice and discrimination",
          type: "video",
          duration: 35
        },
        {
          id: "stereotype-formation",
          title: "Stereotype Formation and Maintenance",
          description: "How stereotypes develop, persist, and influence our perceptions of others",
          type: "text",
          duration: 45,
          textContent: `# Stereotype Formation and Maintenance

## Understanding Prejudice and Stereotyping

### Definitions and Distinctions

**Stereotypes**
- **Definition**: Cognitive generalizations about groups of people
- **Content**: Beliefs about characteristics of group members
- **Function**: Simplify social perception and categorization
- **Examples**: "Athletes are not intelligent," "Elderly people are frail"

**Prejudice**
- **Definition**: Negative attitudes toward groups and their members
- **Components**: Cognitive (stereotypes), affective (emotions), behavioral (discrimination)
- **Direction**: Usually negative, but can be positive
- **Examples**: Sexism, racism, ageism, homophobia

**Discrimination**
- **Definition**: Unfair treatment of individuals based on group membership
- **Manifestation**: Behaviors that advantage or disadvantage groups
- **Types**: Individual vs. institutional discrimination
- **Examples**: Hiring bias, unequal service, hate crimes

**Relationship Between Components**
- **Not always consistent**: Can have stereotype without prejudice
- **Cognitive-affective link**: Stereotypes can trigger emotions
- **Attitude-behavior gap**: Prejudice doesn't always lead to discrimination

### Origins of Stereotypes

**Cognitive Sources**

**Social Categorization**
- **Basic process**: Dividing social world into groups
- **Benefits**: Simplifies complex social environment
- **Consequences**: Accentuates between-group differences
- **In-group/out-group**: "Us" vs. "them" distinctions

**Illusory Correlation**
- **Definition**: Perceiving relationships that don't exist
- **Mechanism**: Distinctive events capture attention
- **Example**: Remembering rare negative behaviors by minority groups
- **Result**: Overestimation of negative traits in minorities

**Confirmation Bias**
- **Selective attention**: Notice stereotype-confirming information
- **Selective interpretation**: Ambiguous behavior seen as confirming
- **Selective memory**: Better recall for stereotype-consistent information
- **Self-fulfilling prophecy**: Expectations influence behavior

**Availability Heuristic**
- **Media representation**: Overrepresentation of certain groups in crime stories
- **Personal experience**: Limited contact leads to biased samples
- **Vivid examples**: Memorable cases influence generalizations
- **Recency effects**: Recent examples seem more representative

### Motivational Sources

**Social Identity Theory**

**Henri Tajfel and John Turner**
People derive part of their self-concept from group memberships and are motivated to maintain positive group identity.

**Key Principles**
- **Social categorization**: Classify self and others into groups
- **Social identification**: Adopt group identity and conform to norms
- **Social comparison**: Compare in-group favorably to out-groups
- **Positive distinctiveness**: Seek ways in-group is superior

**In-group Favoritism**
- **Preferential treatment**: Better evaluation of in-group members
- **Resource allocation**: Give more rewards to in-group
- **Benefit of doubt**: More charitable interpretations
- **Occurs even**: In minimal group situations

**Out-group Homogeneity Effect**
- **"They're all alike"**: See out-group members as more similar
- **In-group diversity**: Recognize individual differences within own group
- **Contact quantity**: Less contact with out-group members
- **Attention patterns**: Focus on category-defining features

**System Justification Theory**

**John Jost**
People are motivated to defend and justify the existing social system, even when it disadvantages them.

**Mechanisms**
- **Status quo bias**: Preference for current arrangements
- **Just-world beliefs**: Assumption that people get what they deserve
- **Complementary stereotypes**: Positive traits offset negative ones
- **Palliative function**: Reduces anxiety about inequality

### Cultural and Social Sources

**Social Learning**
- **Family socialization**: Parents transmit attitudes to children
- **Peer influence**: Friends and social groups shape beliefs
- **Media exposure**: Television, movies, news shape perceptions
- **Educational content**: Textbooks and curricula influence knowledge

**Cultural Stereotypes**
- **Shared beliefs**: Common stereotypes within cultures
- **Historical origins**: Based on past conflicts or interactions
- **Cultural values**: Reflect what society values or fears
- **Transmission**: Passed down through generations

**Institutional Factors**
- **Segregation**: Limited contact between groups
- **Economic competition**: Competition for resources
- **Political rhetoric**: Leaders' statements influence attitudes
- **Legal systems**: Laws that create or maintain inequalities

## Stereotype Maintenance

### Cognitive Processes

**Selective Processing**
- **Attention**: Notice stereotype-confirming information more
- **Interpretation**: Ambiguous behavior seen through stereotype lens
- **Memory**: Better recall for stereotype-consistent information
- **Encoding**: Store information in stereotype-relevant ways

**Subtyping**
- **Definition**: Creating subcategories for stereotype-inconsistent members
- **Function**: Protects overall stereotype from disconfirming evidence
- **Example**: "She's not like other women" or "He's one of the good ones"
- **Result**: Stereotype remains unchanged despite contrary evidence

**Attribution Patterns**
- **Internal attributions**: Negative out-group behavior due to personality
- **External attributions**: Positive out-group behavior due to situation
- **Opposite pattern**: For in-group members
- **Ultimate attribution error**: Systematic bias in causal explanations

### Social Processes

**Stereotype Threat**

**Claude Steele**
When people fear confirming negative stereotypes about their group, anxiety can actually impair their performance.

**Mechanisms**
- **Anxiety**: Worry about confirming stereotype
- **Cognitive load**: Mental resources devoted to managing anxiety
- **Performance impairment**: Reduced performance on stereotype-relevant tasks
- **Self-fulfilling prophecy**: Poor performance seems to confirm stereotype

**Examples**
- **Academic performance**: Women in math, minorities in academics
- **Athletic performance**: Whites in basketball, blacks in swimming
- **Professional settings**: Women in leadership, minorities in technical fields

**Reducing Stereotype Threat**
- **Reframe tasks**: Emphasize learning rather than evaluation
- **Provide role models**: Show successful group members
- **Affirm values**: Self-affirmation exercises
- **Change environment**: Remove stereotype-relevant cues

**Self-Fulfilling Prophecies**
- **Expectation effects**: Stereotypes influence expectations
- **Behavioral confirmation**: Targets behave to confirm expectations
- **Perceiver behavior**: Treat targets according to stereotypes
- **Cycle continuation**: Behavior reinforces original stereotype

## Consequences of Stereotyping

### Individual Level

**Targets of Stereotypes**
- **Identity threat**: Questioning of self-worth and abilities
- **Performance anxiety**: Worry about confirming stereotypes
- **Disengagement**: Withdrawal from stereotype-relevant domains
- **Internalization**: Accepting negative stereotypes about own group

**Perceivers**
- **Biased judgments**: Inaccurate evaluations of individuals
- **Missed opportunities**: Overlooking qualified candidates
- **Relationship quality**: Reduced intimacy and trust
- **Moral costs**: Guilt and anxiety about prejudiced thoughts

### Group Level

**Intergroup Relations**
- **Increased conflict**: Stereotypes fuel group tensions
- **Reduced cooperation**: Less willingness to work together
- **Communication barriers**: Misunderstanding and mistrust
- **Polarization**: Groups become more extreme in their views

**Social Inequality**
- **Employment discrimination**: Biased hiring and promotion
- **Educational gaps**: Different expectations and opportunities
- **Health disparities**: Biased treatment in healthcare
- **Criminal justice**: Disparities in arrests, sentencing

### Societal Level

**Institutional Discrimination**
- **Systemic bias**: Policies and practices that disadvantage groups
- **Structural inequality**: Different access to resources and opportunities
- **Cultural reproduction**: Stereotypes maintain existing hierarchies
- **Economic costs**: Underutilization of human resources

## Reducing Prejudice and Stereotyping

### Contact Hypothesis

**Gordon Allport (1954)**
Under certain conditions, contact between groups can reduce prejudice.

**Optimal Contact Conditions**
- **Equal status**: Groups have similar power and status
- **Common goals**: Groups work toward shared objectives
- **Cooperation**: Groups must work together, not compete
- **Institutional support**: Authorities endorse contact

**Types of Contact**
- **Direct contact**: Face-to-face interaction
- **Extended contact**: Knowing in-group members who have out-group friends
- **Vicarious contact**: Observing positive intergroup contact
- **Imagined contact**: Mental simulation of positive contact

**Mechanisms**
- **Learning**: Gaining accurate information about out-group
- **Affective change**: Developing positive emotions toward out-group
- **Perspective-taking**: Understanding out-group point of view
- **Anxiety reduction**: Decreased intergroup anxiety

### Cognitive Interventions

**Perspective-Taking**
- **Empathy**: Understanding others' experiences and feelings
- **Role reversal**: Imagining being in out-group member's position
- **Benefits**: Increased helping, reduced stereotyping
- **Applications**: Education, diversity training

**Individuation**
- **Focus on individuals**: Attend to unique characteristics
- **Reduce categorization**: Minimize group-based thinking
- **Personal information**: Learn about individual qualities
- **Relationship building**: Develop personal connections

**Counter-stereotypic Examples**
- **Exposure**: Present examples that contradict stereotypes
- **Multiple examples**: Use many different counter-examples
- **Typical members**: Show that counter-examples are not unusual
- **Elaboration**: Encourage thinking about why stereotypes are wrong

### Educational Approaches

**Multicultural Education**
- **Cultural awareness**: Learn about different groups
- **Historical context**: Understand origins of stereotypes
- **Critical thinking**: Question assumptions and biases
- **Skill building**: Develop intercultural competence

**Cooperative Learning**
- **Jigsaw classroom**: Students depend on each other for learning
- **Mixed groups**: Diverse teams working together
- **Shared goals**: Common objectives that require cooperation
- **Success**: Positive outcomes from working together

Understanding prejudice and stereotyping helps us recognize bias in ourselves and others, and work toward more equitable and inclusive communities.`
        },
        {
          id: "bias-reduction",
          title: "Bias Reduction Exercise",
          description: "Practice identifying and countering stereotypes and prejudices",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "bias-awareness",
            instructions: "Examine scenarios for stereotyping and prejudice, and develop strategies for reducing bias.",
            scenarios: [
              {
                id: "hiring-bias",
                situation: "A hiring manager consistently rates candidates with foreign names lower than those with American names, despite similar qualifications",
                bias: "Name-based discrimination",
                mechanisms: ["Unconscious bias", "In-group favoritism", "Cultural stereotypes"],
                interventions: ["Blind resume review", "Structured interviews", "Bias training", "Diverse hiring panels"]
              },
              {
                id: "classroom-expectations",
                situation: "A teacher calls on male students more often in math class and female students more often in language arts",
                bias: "Gender stereotyping",
                mechanisms: ["Stereotype threat", "Confirmation bias", "Differential expectations"],
                interventions: ["Equal participation tracking", "Growth mindset training", "Diverse role models", "Bias awareness"]
              }
            ]
          }
        }
      ],
      quiz: {
        id: "prejudice-stereotyping-quiz",
        title: "Prejudice and Stereotyping Quiz",
        description: "Test your understanding of prejudice, stereotyping, and discrimination",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Stereotype threat refers to:",
            options: [
              "The threat of being stereotyped by others",
              "Anxiety about confirming negative stereotypes about one's group",
              "The tendency to stereotype threatening groups",
              "Using stereotypes to threaten out-group members"
            ],
            correctAnswer: 1,
            explanation: "Stereotype threat is the anxiety people feel when they risk confirming negative stereotypes about their group, which can impair performance."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "According to Allport's contact hypothesis, prejudice is most likely to be reduced when contact involves:",
            options: [
              "Competition between groups",
              "Unequal status and cooperation toward common goals",
              "Equal status and cooperation toward common goals",
              "Separate activities for different groups"
            ],
            correctAnswer: 2,
            explanation: "Allport's contact hypothesis states that prejudice is reduced when groups have equal status and cooperate toward common goals with institutional support."
          }
        ]
      }
    },
    {
      id: "aggression-prosocial",
      title: "Aggression and Prosocial Behavior",
      description: "Understanding the causes of aggression and the psychology of helping behavior",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "aggression-intro",
          title: "Understanding Aggression",
          description: "Types, theories, and causes of aggressive behavior",
          type: "video",
          duration: 35
        },
        {
          id: "aggression-theories",
          title: "Theories of Aggression and Helping",
          description: "Comprehensive examination of aggressive and prosocial behavior theories",
          type: "text",
          duration: 50,
          textContent: `# Theories of Aggression and Helping

## Understanding Aggression

### Definition and Types

**Aggression Defined**
Intentional behavior aimed at causing harm to another person who is motivated to avoid such harm.

**Key Components**
- **Intentionality**: Deliberate attempt to harm
- **Harm**: Physical or psychological damage
- **Motivation to avoid**: Target doesn't want to be harmed
- **Not accidental**: Excludes unintentional harm

**Types of Aggression**

**Physical vs. Verbal**
- **Physical aggression**: Hitting, kicking, destroying property
- **Verbal aggression**: Insults, threats, yelling
- **Relational aggression**: Damaging relationships, social exclusion
- **Cyber aggression**: Online harassment, cyberbullying

**Hostile vs. Instrumental**
- **Hostile aggression**: Driven by anger, goal is to harm
- **Instrumental aggression**: Means to achieve another goal
- **Example**: Road rage (hostile) vs. military action (instrumental)
- **Mixed motives**: Often both elements present

**Direct vs. Indirect**
- **Direct aggression**: Face-to-face confrontation
- **Indirect aggression**: Behind-the-scenes harm (gossip, exclusion)
- **Gender differences**: Males more direct, females more indirect
- **Cultural variations**: Different norms for expression

### Biological Theories

**Evolutionary Perspectives**

**Survival and Reproduction**
- **Resource competition**: Fighting for food, territory, mates
- **Dominance hierarchies**: Establishing social status
- **Parental investment**: Protecting offspring
- **Sexual selection**: Competition for reproductive opportunities

**Male Warrior Hypothesis**
- **Intergroup aggression**: Males evolved for warfare
- **Coalitional psychology**: Form alliances against threats
- **Evidence**: Male-male competition, warfare patterns
- **Modern implications**: Sports, business competition

**Neurobiological Factors**

**Brain Structures**
- **Amygdala**: Fear and threat processing
- **Prefrontal cortex**: Impulse control and regulation
- **Hypothalamus**: Fight-or-flight responses
- **Damage effects**: Lesions can increase aggression

**Neurotransmitters**
- **Serotonin**: Lower levels associated with aggression
- **Dopamine**: Reward seeking and motivation
- **Testosterone**: Linked to dominance and aggression
- **GABA**: Inhibitory effects on aggressive impulses

**Genetic Influences**
- **Twin studies**: Heritability estimates around 50%
- **MAOA gene**: "Warrior gene" variants
- **Gene-environment interaction**: Genes + environment
- **Epigenetics**: Environmental effects on gene expression

### Psychological Theories

**Social Learning Theory**

**Albert Bandura**
Aggression is learned through observation, imitation, and reinforcement.

**Observational Learning**
- **Modeling**: Watching others' aggressive behavior
- **Vicarious reinforcement**: Seeing aggression rewarded
- **Bobo doll studies**: Children imitate adult aggression
- **Media influences**: Television, movies, video games

**Reinforcement Processes**
- **Direct reinforcement**: Aggression leads to desired outcomes
- **Vicarious reinforcement**: Observing others being rewarded
- **Self-reinforcement**: Internal satisfaction from aggression
- **Punishment effects**: Inconsistent or delayed punishment

**Cognitive Scripts**
- **Aggressive scripts**: Learned patterns of aggressive behavior
- **Activation**: Triggered by situational cues
- **Rehearsal**: Mental practice strengthens scripts
- **Automaticity**: Scripts become habitual responses

**Frustration-Aggression Hypothesis**

**Dollard et al. (1939)**
Frustration always leads to aggression, and aggression is always the result of frustration.

**Original Hypothesis**
- **Frustration**: Blocking of goal-directed behavior
- **Inevitable aggression**: Frustration always produces aggressive drive
- **Drive reduction**: Aggression reduces frustration

**Modifications**
- **Berkowitz revision**: Frustration leads to anger, not directly to aggression
- **Negative affect**: Any negative emotion can trigger aggression
- **Cue-dependent**: Environmental cues influence expression
- **Individual differences**: Not everyone responds aggressively

**Social Cognitive Theory**

**Cognitive Neoassociation Model**
- **Aversive events**: Heat, pain, crowding create negative affect
- **Associations**: Negative affect linked to aggressive thoughts
- **Fight-or-flight**: Two possible responses to negative affect
- **Cognitive processing**: Higher-order thinking influences final response

### Environmental Factors

**Situational Triggers**

**Heat Hypothesis**
- **Temperature effects**: Higher temperatures increase aggression
- **Violent crime**: More frequent in hot weather
- **Laboratory studies**: Heat increases hostile thoughts
- **Curvilinear**: Very high temperatures may reduce aggression

**Crowding and Noise**
- **Crowding stress**: Invasion of personal space
- **Noise effects**: Loud, unpredictable sounds increase irritation
- **Urban environments**: Higher aggression in dense cities
- **Individual differences**: Some people more sensitive

**Alcohol and Drugs**
- **Disinhibition**: Reduced impulse control
- **Cognitive impairment**: Poor decision-making
- **Expectancy effects**: Beliefs about alcohol's effects
- **Social context**: Environment influences alcohol's impact

**Weapons Effect**
- **Berkowitz and LePage**: Presence of weapons increases aggression
- **Priming**: Weapons activate aggressive thoughts
- **Availability**: Easy access to weapons increases violence
- **Cultural differences**: Weapon availability varies by culture

## Prosocial Behavior

### Definition and Types

**Prosocial Behavior**
Voluntary behavior intended to benefit others.

**Types of Helping**

**Altruism**
- **Definition**: Helping motivated solely by concern for others
- **No expectation**: No expected reward or benefit
- **Empathic concern**: Focus on other's welfare
- **Debate**: Whether true altruism exists

**Egoistic Helping**
- **Self-benefit**: Helping to gain something for oneself
- **Mood enhancement**: Feel good about helping
- **Social approval**: Gain respect and admiration
- **Reciprocity**: Expect help in return

**Cooperative Behavior**
- **Mutual benefit**: Both helper and recipient benefit
- **Group goals**: Working together for common objectives
- **Interdependence**: Success depends on cooperation
- **Examples**: Teamwork, community projects

### Theories of Helping

**Evolutionary Approaches**

**Kin Selection**
- **Hamilton's rule**: Help relatives who share genes
- **Inclusive fitness**: Survival of shared genetic material
- **Coefficient of relatedness**: Closer relatives more likely to be helped
- **Evidence**: Parents sacrifice for children

**Reciprocal Altruism**
- **Robert Trivers**: Help others who might help you later
- **Delayed reciprocity**: Help now, benefit later
- **Reputation**: Being known as helpful brings benefits
- **Cheater detection**: Ability to identify non-reciprocators

**Group Selection**
- **Group benefits**: Helping behavior benefits the group
- **Between-group competition**: Cooperative groups outcompete selfish groups
- **Cultural evolution**: Helpful norms spread through groups
- **Controversial**: Debate over group vs. individual selection

**Social Exchange Theory**

**Cost-Benefit Analysis**
- **Helping decision**: Based on costs vs. benefits
- **Personal costs**: Time, effort, risk, resources
- **Personal benefits**: Good feelings, social approval, reciprocity
- **Rational choice**: People help when benefits exceed costs

**Arousal: Cost-Reward Model**
- **Arousal**: Witnessing suffering creates unpleasant arousal
- **Cost-reward matrix**: Consider costs of helping vs. not helping
- **Arousal reduction**: Main goal is to reduce unpleasant feelings
- **Multiple strategies**: Helping is one way to reduce arousal

**Empathy-Altruism Hypothesis**

**Daniel Batson**
Empathic concern for others leads to truly altruistic helping.

**Two Pathways**
- **Personal distress**: Feeling upset by other's suffering (egoistic)
- **Empathic concern**: Feeling compassion for other's suffering (altruistic)
- **Different motivations**: Distress reduction vs. welfare improvement
- **Different behaviors**: Escape vs. helping

**Evidence**
- **Escape studies**: When escape is easy, personal distress leads to escape
- **Empathy manipulations**: High empathy leads to helping even when escape is easy
- **Neuroimaging**: Different brain patterns for distress vs. empathy
- **Criticism**: Debate over whether empathy is truly altruistic

### Factors Influencing Helping

**Bystander Effect**

**Kitty Genovese Case**
- **1964 incident**: Woman attacked while neighbors watched
- **Initial reports**: 38 witnesses did nothing
- **Later investigation**: Fewer witnesses, some did call police
- **Scientific interest**: Led to extensive research on bystander behavior

**Darley and Latané Model**

**Five-Step Decision Process**
1. **Notice the event**: Must be aware something is happening
2. **Interpret as emergency**: Recognize that help is needed
3. **Take responsibility**: Feel personally responsible to act
4. **Know how to help**: Have skills or knowledge to assist
5. **Decide to implement**: Choose to actually help

**Diffusion of Responsibility**
- **Multiple bystanders**: Responsibility spread among observers
- **Less personal responsibility**: Each person feels less obligation
- **Group size effect**: More bystanders, less likely to help
- **Mechanism**: "Someone else will help"

**Pluralistic Ignorance**
- **Ambiguous situations**: Unclear if help is needed
- **Look to others**: Use others' reactions to interpret situation
- **Calm appearances**: Others may also be uncertain
- **Misinterpretation**: Assume no emergency if others aren't reacting

**Evaluation Apprehension**
- **Fear of judgment**: Worry about looking foolish
- **Social embarrassment**: Concern about making mistakes
- **Competence concerns**: Doubt about ability to help effectively
- **Public vs. private**: More helping when not being watched

### Individual Differences in Helping

**Personality Factors**

**Empathy**
- **Perspective-taking**: Ability to see others' viewpoints
- **Empathic concern**: Feeling compassion for others
- **Personal distress**: Being upset by others' suffering
- **Development**: Increases with age and experience

**Moral Development**
- **Kohlberg's stages**: Higher stages associated with more helping
- **Principled conscience**: Internalized moral principles
- **Care orientation**: Focus on relationships and responsibilities
- **Moral identity**: Seeing oneself as moral person

**Religiosity**
- **Religious involvement**: Associated with more helping
- **Religious teachings**: Emphasis on helping others
- **Community support**: Religious communities encourage helping
- **In-group bias**: May help co-religionists more

**Demographics**

**Gender Differences**
- **Type of help**: Men more likely to help in emergencies (heroic helping)
- **Women**: More likely to provide ongoing support (nurturant helping)
- **Social norms**: Different expectations for men and women
- **Situational factors**: Context influences gender differences

**Age Effects**
- **Children**: Helping increases with age and cognitive development
- **Adults**: Generally stable levels of helping
- **Elderly**: May have more time but less physical ability
- **Generational differences**: Cohort effects in helping behavior

### Increasing Prosocial Behavior

**Educational Approaches**
- **Empathy training**: Teach perspective-taking skills
- **Moral education**: Discuss ethical principles and values
- **Service learning**: Combine education with community service
- **Role models**: Provide examples of helpful behavior

**Environmental Design**
- **Reduce anonymity**: Help people feel noticed and connected
- **Clear responsibility**: Assign specific roles and duties
- **Social norms**: Make helping behavior visible and valued
- **Feedback**: Show impact of helping behavior

**Institutional Policies**
- **Volunteer programs**: Organize opportunities to help
- **Recognition systems**: Reward and celebrate helping
- **Professional requirements**: Make helping part of job duties
- **Legal protections**: Good Samaritan laws reduce liability

Understanding aggression and prosocial behavior helps us create environments that reduce harmful behavior and promote helping and cooperation.`
        },
        {
          id: "helping-scenarios",
          title: "Helping Behavior Analysis",
          description: "Analyze factors that influence helping behavior in different situations",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "helping-analysis",
            instructions: "Examine scenarios and identify factors that increase or decrease the likelihood of helping behavior.",
            scenarios: [
              {
                id: "emergency-help",
                situation: "A person collapses on a busy city street during rush hour",
                factors: {
                  decreasing: ["Diffusion of responsibility", "Pluralistic ignorance", "Urban anonymity", "Time pressure"],
                  increasing: ["Clear emergency", "Visible distress", "Potential for heroic helping"]
                },
                predictions: "Lower likelihood due to bystander effect",
                interventions: ["Make eye contact and ask specific person to help", "Clearly state it's an emergency"]
              },
              {
                id: "charity-request",
                situation: "Someone asks for a donation to help homeless individuals",
                factors: {
                  decreasing: ["Possible scam concerns", "Financial costs", "Unclear need"],
                  increasing: ["Empathy for homeless", "Social approval", "Religious/moral values"]
                },
                predictions: "Moderate likelihood depending on individual factors",
                interventions: ["Provide specific information about need", "Show impact of donations"]
              }
            ]
          }
        }
      ],
      quiz: {
        id: "aggression-prosocial-quiz",
        title: "Aggression and Prosocial Behavior Quiz",
        description: "Test your understanding of aggressive and helping behavior",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "The bystander effect suggests that:",
            options: [
              "People are more likely to help when alone than in groups",
              "People are more likely to help when in groups than alone",
              "Group size doesn't affect helping behavior",
              "Only trained professionals should help in emergencies"
            ],
            correctAnswer: 0,
            explanation: "The bystander effect shows that people are less likely to help when others are present due to diffusion of responsibility and pluralistic ignorance."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "According to the empathy-altruism hypothesis, helping motivated by empathic concern is:",
            options: [
              "Ultimately selfish",
              "Truly altruistic",
              "Only seen in close relationships",
              "Rare in humans"
            ],
            correctAnswer: 1,
            explanation: "The empathy-altruism hypothesis proposes that empathic concern leads to truly altruistic helping, motivated by genuine care for others' welfare."
          }
        ]
      }
    },
    {
      id: "relationships-attraction",
      title: "Interpersonal Relationships and Attraction",
      description: "Psychology of attraction, relationship formation, and maintenance",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "attraction-intro",
          title: "Psychology of Attraction",
          description: "What makes people attractive to each other and how relationships form",
          type: "video",
          duration: 30
        },
        {
          id: "relationship-development",
          title: "Relationship Formation and Development",
          description: "How relationships begin, develop, and are maintained over time",
          type: "text",
          duration: 50,
          textContent: `# Relationship Formation and Development

## Understanding Attraction

### Physical Attractiveness

**The Power of Beauty**
Physical attractiveness is one of the strongest predictors of initial attraction and romantic interest.

**Cross-Cultural Standards**
- **Facial symmetry**: Universally considered attractive
- **Clear skin**: Indicator of health and youth
- **Average features**: Faces close to population average are attractive
- **Cultural variations**: Some beauty standards vary by culture

**Evolutionary Explanations**
- **Health indicators**: Attractive features signal good health
- **Fertility cues**: Features associated with reproductive potential
- **Good genes**: Attractive people may have superior genetic quality
- **Parental investment**: Resources available for offspring

**Halo Effect**
- **Positive assumptions**: Attractive people seen as more intelligent, kind, successful
- **Self-fulfilling prophecy**: Better treatment leads to better outcomes
- **Dating advantage**: More romantic opportunities
- **Professional benefits**: Better job prospects and higher salaries

**Matching Hypothesis**
- **Similar attractiveness**: People tend to partner with others of similar physical attractiveness
- **Realistic choices**: Choose partners within own "league"
- **Evidence**: Couples tend to be matched on attractiveness ratings
- **Exceptions**: Other resources can compensate for lower attractiveness

### Proximity and Familiarity

**Propinquity Effect**
- **Physical closeness**: We like people who are geographically close
- **Repeated exposure**: Familiarity breeds liking
- **Westgate studies**: Friendships formed based on apartment proximity
- **Functional distance**: How often paths cross matters more than actual distance

**Mere Exposure Effect**
- **Robert Zajonc**: Repeated exposure increases liking
- **No interaction needed**: Even passive exposure increases attraction
- **Laboratory studies**: Exposure to faces, names, sounds increases preference
- **Limits**: Effect reverses with overexposure or negative associations

**Availability**
- **Contact opportunities**: Must have chances to interact
- **Shared environments**: Work, school, neighborhood
- **Activity overlap**: Common interests and activities
- **Modern technology**: Online platforms create virtual proximity

### Similarity

**Attitude Similarity**
- **Attraction to similar others**: Shared beliefs and values increase liking
- **Validation**: Similar others confirm our worldview
- **Byrne's law**: Attraction proportional to proportion of similar attitudes
- **Important attitudes**: Core values matter more than trivial preferences

**Demographic Similarity**
- **Homogamy**: Tendency to marry within same group
- **Age**: Usually within few years
- **Race/ethnicity**: High levels of within-group marriage
- **Religion**: Shared faith important to many
- **Social class**: Education and income similarity
- **Personality**: Some traits show similarity, others complementarity

**Activity Similarity**
- **Shared interests**: Common hobbies and activities
- **Lifestyle compatibility**: Similar daily routines and preferences
- **Value alignment**: Agreement on important life goals
- **Communication styles**: Compatible ways of interacting

### Complementarity

**Complementary Needs**
- **Winch's theory**: Opposites attract when needs complement
- **Dominance-submission**: Dominant person with submissive partner
- **Nurturance-succorance**: Caring person with dependent partner
- **Limited evidence**: Research support is weak

**Skill Complementarity**
- **Different strengths**: Partners contribute different abilities
- **Task specialization**: Division of labor in relationships
- **Mutual benefit**: Each partner gains from other's skills
- **Examples**: One social, one organized; one creative, one practical

## Relationship Development

### Stages of Relationship Development

**Knapp's Relationship Model**

**Coming Together Stages**

**Initiating**
- **First impressions**: Physical appearance and initial interaction
- **Surface-level**: Basic demographic information
- **Politeness**: Formal, conventional behavior
- **Goal**: Determine if further interaction is desired

**Experimenting**
- **Small talk**: Light conversation to learn about other person
- **Common ground**: Looking for similarities and shared interests
- **Low risk**: Safe topics and limited self-disclosure
- **Many relationships**: Most stay at this level

**Intensifying**
- **Deeper disclosure**: Share more personal information
- **Increased contact**: More frequent and longer interactions
- **Intimacy markers**: Pet names, inside jokes, shared activities
- **Relationship definition**: "We're together" or "We're dating"

**Integrating**
- **Couple identity**: Seen as a unit by others
- **Shared social network**: Common friends and activities
- **Interdependence**: Lives become intertwined
- **"We" language**: Talk about "us" rather than "me"

**Bonding**
- **Public commitment**: Marriage, engagement, or formal partnership
- **Institutional recognition**: Legal or ceremonial acknowledgment
- **Long-term commitment**: Plans for shared future
- **Exclusive relationship**: Commitment to each other only

### Self-Disclosure

**Social Penetration Theory**

**Altman and Taylor**
Relationships develop through gradual increase in breadth and depth of self-disclosure.

**Breadth**
- **Number of topics**: Range of subjects discussed
- **Surface level**: Many topics at shallow level initially
- **Expansion**: Gradually include more areas of life
- **Examples**: Work, family, hobbies, beliefs, fears

**Depth**
- **Level of intimacy**: How personal and private information is
- **Gradual increase**: Start with safe topics, move to more personal
- **Central vs. peripheral**: Core aspects of self vs. superficial traits
- **Vulnerability**: Deeper disclosure involves more risk

**Reciprocity**
- **Matching disclosure**: People tend to match others' level of openness
- **Turn-taking**: Back-and-forth sharing of personal information
- **Balance**: Relationships work best with similar disclosure levels
- **Timing**: Disclosure should be appropriate to relationship stage

**Benefits of Self-Disclosure**
- **Intimacy building**: Creates closeness and connection
- **Validation**: Acceptance despite vulnerabilities
- **Understanding**: Better knowledge of partner
- **Trust**: Demonstrates confidence in relationship

**Risks of Self-Disclosure**
- **Rejection**: Partner may not accept disclosed information
- **Vulnerability**: Information could be used against you
- **Inappropriate timing**: Too much too soon can damage relationship
- **Imbalance**: One-sided disclosure creates discomfort

### Love and Intimacy

**Sternberg's Triangular Theory of Love**

**Three Components**

**Intimacy**
- **Emotional closeness**: Feeling connected and bonded
- **Communication**: Open, honest sharing
- **Support**: Being there for each other
- **Affection**: Warmth and caring feelings

**Passion**
- **Physical attraction**: Sexual and romantic desire
- **Excitement**: Intense emotional arousal
- **Infatuation**: Obsessive thoughts about partner
- **Chemistry**: Strong physical and emotional pull

**Commitment**
- **Decision**: Choice to love and stay with partner
- **Dedication**: Working to maintain relationship
- **Investment**: Time, energy, and resources put into relationship
- **Future orientation**: Plans and goals together

**Types of Love**
- **Intimacy only**: Liking (close friendship)
- **Passion only**: Infatuation ("love at first sight")
- **Commitment only**: Empty love (arranged marriage without feeling)
- **Intimacy + Passion**: Romantic love (new relationships)
- **Intimacy + Commitment**: Companionate love (long marriages)
- **Passion + Commitment**: Fatuous love (whirlwind romance)
- **All three**: Consummate love (ideal complete love)

**Love Development**
- **Passion peaks early**: Intense at beginning, fades over time
- **Intimacy grows gradually**: Increases with time and experience
- **Commitment develops slowly**: Builds through shared experiences
- **Cultural differences**: Arranged vs. choice marriages show different patterns

### Attachment Styles

**Bowlby's Attachment Theory**
Early relationships with caregivers create internal working models that influence adult relationships.

**Adult Attachment Styles**

**Secure Attachment (60%)**
- **Characteristics**: Comfortable with intimacy and independence
- **Relationships**: Trusting, supportive, lasting
- **Communication**: Direct, open, effective
- **Conflict**: Handle disagreements constructively

**Anxious-Preoccupied (20%)**
- **Characteristics**: Want close relationships but fear abandonment
- **Relationships**: Clingy, jealous, demanding
- **Communication**: Emotional, seeking reassurance
- **Conflict**: Escalate conflicts, catastrophize problems

**Dismissive-Avoidant (15%)**
- **Characteristics**: Value independence over relationships
- **Relationships**: Distant, self-reliant, commitment-phobic
- **Communication**: Minimize emotions, avoid deep topics
- **Conflict**: Withdraw, shut down emotionally

**Disorganized-Fearful (5%)**
- **Characteristics**: Want relationships but fear getting hurt
- **Relationships**: Chaotic, hot and cold, unpredictable
- **Communication**: Inconsistent, mixed messages
- **Conflict**: Reactive, may become aggressive or withdraw

**Attachment and Relationships**
- **Partner selection**: Often choose partners with complementary styles
- **Relationship satisfaction**: Secure attachments have best outcomes
- **Stability**: Attachment styles relatively stable but can change
- **Therapy**: Can help people develop more secure attachment

## Relationship Maintenance

### Factors in Relationship Success

**Communication Skills**
- **Active listening**: Paying attention and understanding partner
- **Emotional expression**: Sharing feelings appropriately
- **Conflict resolution**: Handling disagreements constructively
- **Appreciation**: Expressing gratitude and recognition

**Trust and Commitment**
- **Reliability**: Following through on promises and commitments
- **Honesty**: Being truthful even when difficult
- **Loyalty**: Supporting partner and relationship
- **Investment**: Putting effort into relationship success

**Intimacy and Affection**
- **Physical intimacy**: Appropriate touch and sexual connection
- **Emotional intimacy**: Deep sharing and vulnerability
- **Quality time**: Focused attention and shared activities
- **Romance**: Maintaining excitement and special gestures

**Compatibility**
- **Values alignment**: Agreement on important life principles
- **Goal compatibility**: Shared or complementary objectives
- **Lifestyle fit**: Compatible daily routines and preferences
- **Personality match**: Complementary or similar traits

### Relationship Challenges

**Common Problems**
- **Communication breakdown**: Misunderstandings and poor listening
- **Trust issues**: Betrayal, jealousy, dishonesty
- **Intimacy problems**: Physical or emotional distance
- **Life transitions**: Job changes, parenthood, aging
- **External stressors**: Financial problems, family issues

**Gottman's Four Horsemen**

**Predictors of Divorce**
- **Criticism**: Attacking partner's character rather than specific behavior
- **Contempt**: Expressing superiority and disrespect
- **Defensiveness**: Playing victim and counterattacking
- **Stonewalling**: Withdrawing and shutting down

**Positive Interactions**
- **5:1 ratio**: Five positive interactions for every negative one
- **Repair attempts**: Efforts to de-escalate conflicts
- **Accepting influence**: Being open to partner's input
- **Fondness and admiration**: Maintaining positive view of partner

### Online Relationships

**Digital Communication**
- **Reduced cues**: Less nonverbal information
- **Asynchronous**: Can think before responding
- **Disinhibition**: May share more online than in person
- **Idealization**: Fill in missing information positively

**Relationship Formation**
- **Similarity focus**: Easier to find people with shared interests
- **Gradual disclosure**: Can control pace of self-revelation
- **Physical appearance**: Less initial focus on looks
- **Geographic barriers**: Can connect across distances

**Challenges**
- **Deception**: Easier to misrepresent oneself
- **Lack of chemistry**: Hard to assess physical attraction
- **Context collapse**: Online persona vs. real-life behavior
- **Transition difficulty**: Moving from online to offline

Understanding relationships helps us build stronger connections with others and navigate the complexities of human social bonds.`
        },
        {
          id: "relationship-analysis",
          title: "Relationship Dynamics Analysis",
          description: "Analyze factors that contribute to successful and unsuccessful relationships",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "relationship-analysis",
            instructions: "Examine relationship scenarios and identify factors that promote or hinder relationship success.",
            scenarios: [
              {
                id: "new-relationship",
                stage: "Early Dating",
                situation: "Two people who met online have been dating for 2 months and are considering becoming exclusive",
                positive_factors: ["Shared values and interests", "Good communication", "Physical attraction", "Similar life goals"],
                negative_factors: ["Limited in-person time", "Different social circles", "Uncertainty about commitment"],
                recommendations: ["Increase face-to-face time", "Meet each other's friends", "Discuss expectations openly"]
              },
              {
                id: "long-term-marriage",
                stage: "Established Marriage",
                situation: "Married couple of 15 years feeling distant and considering counseling",
                positive_factors: ["Shared history", "Commitment to marriage", "Good co-parenting", "Financial stability"],
                negative_factors: ["Poor communication", "Lack of intimacy", "Work stress", "Different interests"],
                recommendations: ["Couples therapy", "Schedule quality time", "Improve communication skills", "Rediscover shared activities"]
              }
            ]
          }
        }
      ],
      quiz: {
        id: "relationships-attraction-quiz",
        title: "Relationships and Attraction Quiz",
        description: "Test your understanding of attraction, relationship formation, and maintenance",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Sternberg's triangular theory, consummate love consists of:",
            options: [
              "Intimacy and passion only",
              "Passion and commitment only",
              "Intimacy and commitment only",
              "Intimacy, passion, and commitment"
            ],
            correctAnswer: 3,
            explanation: "Consummate love in Sternberg's model includes all three components: intimacy (emotional closeness), passion (physical attraction), and commitment (decision to maintain the relationship)."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "The mere exposure effect suggests that:",
            options: [
              "Familiarity breeds contempt",
              "Repeated exposure increases liking",
              "First impressions are most important",
              "Opposites attract"
            ],
            correctAnswer: 1,
            explanation: "The mere exposure effect demonstrates that repeated exposure to a person, object, or stimulus tends to increase our liking for it, even without direct interaction."
          }
        ]
      }
    },
    {
      id: "social-cognition",
      title: "Social Cognition and Person Perception",
      description: "How we think about and perceive other people in social situations",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "social-cognition-intro",
          title: "Introduction to Social Cognition",
          description: "How we process social information and form impressions of others",
          type: "video",
          duration: 35
        },
        {
          id: "attribution-theory",
          title: "Attribution Theory and Social Perception",
          description: "How we explain behavior and form impressions of others",
          type: "text",
          duration: 50,
          textContent: `# Attribution Theory and Social Perception

## Understanding Social Cognition

### What is Social Cognition?

Social cognition refers to how people process, store, and apply information about other people and social situations.

**Key Components**
- **Attention**: What social information we notice
- **Encoding**: How we interpret and store social information
- **Storage**: How social knowledge is organized in memory
- **Retrieval**: How we access social information when needed
- **Application**: How we use social knowledge to make judgments

**Importance**
- **Survival**: Need to quickly assess others' intentions
- **Social navigation**: Understanding social situations and relationships
- **Prediction**: Anticipating others' behavior
- **Interaction**: Guiding our own social behavior

### Attribution Theory

**Definition**
Attributions are explanations we make for behavior - our own and others'.

**Fritz Heider (1958)**
Father of attribution theory; distinguished between internal and external causes of behavior.

**Types of Attributions**

**Internal (Dispositional) Attributions**
- **Personality traits**: "She's helpful because she's a kind person"
- **Abilities**: "He succeeded because he's intelligent"
- **Attitudes**: "They protested because they care about the environment"
- **Effort**: "She failed because she didn't try hard enough"

**External (Situational) Attributions**
- **Environmental factors**: "He's quiet because it's a library"
- **Social pressure**: "She agreed because everyone else did"
- **Luck**: "They won because they got lucky"
- **Task difficulty**: "He failed because the test was too hard"

### Kelley's Covariation Model

**Harold Kelley (1967)**
People make attributions by examining how behavior covaries with potential causes.

**Three Types of Information**

**Consensus**
- **Question**: Do other people behave the same way in this situation?
- **High consensus**: Most people behave this way
- **Low consensus**: Few people behave this way
- **Example**: Everyone laughs at the comedian (high) vs. only one person laughs (low)

**Distinctiveness**
- **Question**: Does this person behave differently in other situations?
- **High distinctiveness**: Behavior is unique to this situation
- **Low distinctiveness**: Person behaves this way in many situations
- **Example**: Only laughs at this comedian (high) vs. laughs at all comedians (low)

**Consistency**
- **Question**: Does this person always behave this way in this situation?
- **High consistency**: Behavior is stable across time
- **Low consistency**: Behavior varies across time
- **Example**: Always laughs at this comedian (high) vs. sometimes laughs (low)

**Attribution Patterns**

**Internal Attribution**
- **Low consensus**: Few others behave this way
- **Low distinctiveness**: Person behaves this way in many situations
- **High consistency**: Behavior is stable across time
- **Conclusion**: Behavior reflects person's personality

**External Attribution**
- **High consensus**: Most others behave this way
- **High distinctiveness**: Behavior unique to this situation
- **High consistency**: Behavior stable in this situation
- **Conclusion**: Behavior reflects situational factors

**Unstable Attribution**
- **Low consistency**: Behavior varies across time
- **Conclusion**: Behavior due to temporary factors

### Attribution Biases

**Fundamental Attribution Error**

**Definition**: Tendency to overestimate internal factors and underestimate external factors when explaining others' behavior.

**Demonstrations**
- **Castro essay study**: Participants attributed pro-Castro essay to writer's beliefs even when told writer was assigned position
- **Quiz show study**: Observers rated questioners as more knowledgeable than contestants, ignoring role advantages

**Causes**
- **Perceptual salience**: Person is more visible than situation
- **Cultural emphasis**: Western focus on individual responsibility
- **Cognitive efficiency**: Internal attributions are simpler
- **Just-world belief**: Assumption that people get what they deserve

**Cultural Variations**
- **Western cultures**: Stronger fundamental attribution error
- **Eastern cultures**: More attention to situational factors
- **Collectivist values**: Greater awareness of social context
- **Holistic thinking**: Considering multiple factors simultaneously

**Actor-Observer Bias**

**Definition**: We tend to attribute our own behavior to external factors but others' behavior to internal factors.

**Mechanisms**
- **Information availability**: More aware of our own situational constraints
- **Visual perspective**: See situation when acting, see person when observing
- **Motivation**: Protect self-esteem by making external attributions

**Self-Serving Bias**

**Definition**: Tendency to attribute our successes to internal factors and failures to external factors.

**Examples**
- **Academic performance**: "I got an A because I'm smart" vs. "I got a C because the teacher was unfair"
- **Sports**: "We won because we played well" vs. "We lost because the referees were biased"
- **Relationships**: "The relationship succeeded because I'm a good partner" vs. "It failed because we were incompatible"

**Functions**
- **Self-esteem protection**: Maintains positive self-image
- **Motivation**: Encourages continued effort
- **Depression**: Absence of self-serving bias linked to depression

### Impression Formation

**First Impressions**

**Primacy Effect**
- **Solomon Asch studies**: First traits mentioned have greater impact
- **"Warm" vs. "Cold" study**: Single trait influenced entire impression
- **Anchoring**: First information serves as reference point
- **Adjustment**: Later information adjusts from initial anchor

**Snap Judgments**
- **Speed**: Impressions formed in milliseconds
- **Thin slices**: Brief observations can be surprisingly accurate
- **Nonverbal cues**: Body language and facial expressions
- **Accuracy**: First impressions often predict later judgments

**Central Traits**
- **Warm vs. cold**: Particularly influential in impression formation
- **Evaluative dimension**: Positive vs. negative evaluation
- **Organizing principle**: Other traits interpreted through this lens
- **Context effects**: Same trait interpreted differently in different contexts

### Schemas and Social Categories

**Person Schemas**

**Definition**: Cognitive structures that organize knowledge about people.

**Types of Schemas**
- **Trait schemas**: Knowledge about personality characteristics
- **Role schemas**: Expectations about social roles (teacher, parent, student)
- **Person schemas**: Knowledge about specific individuals
- **Event schemas (scripts)**: Knowledge about social situations

**Functions**
- **Efficiency**: Quick processing of social information
- **Prediction**: Anticipate others' behavior
- **Filling gaps**: Infer missing information
- **Organization**: Structure social knowledge

**Schema Effects**
- **Selective attention**: Notice schema-consistent information
- **Interpretation**: Ambiguous behavior seen through schema lens
- **Memory**: Better recall for schema-consistent information
- **Reconstruction**: Fill in missing details with schema-consistent information

**Stereotypes as Schemas**
- **Group-based schemas**: Knowledge about social groups
- **Automatic activation**: Triggered by category membership
- **Resistance to change**: Maintained through biased processing
- **Individual variation**: Not all group members fit stereotype

### Automatic vs. Controlled Processing

**Automatic Processing**

**Characteristics**
- **Fast**: Occurs rapidly without conscious effort
- **Efficient**: Requires minimal cognitive resources
- **Unconscious**: Often occurs below awareness
- **Difficult to control**: Hard to stop once initiated

**Examples**
- **Stereotype activation**: Automatic categorization of people
- **Emotional reactions**: Immediate feelings about others
- **Nonverbal behavior**: Unconscious mirroring and reactions
- **Implicit attitudes**: Automatic evaluations

**Controlled Processing**

**Characteristics**
- **Slow**: Takes time and conscious effort
- **Effortful**: Requires cognitive resources
- **Conscious**: Deliberate and aware
- **Flexible**: Can be directed and modified

**Examples**
- **Careful evaluation**: Thoughtful consideration of information
- **Stereotype suppression**: Consciously avoiding biased judgments
- **Perspective taking**: Deliberately considering others' viewpoints
- **Complex reasoning**: Weighing multiple factors

**Dual-Process Models**
- **System 1**: Fast, automatic, intuitive
- **System 2**: Slow, controlled, analytical
- **Default**: System 1 unless motivated and able to use System 2
- **Interaction**: Both systems can influence judgments

### Nonverbal Communication

**Facial Expressions**

**Universal Emotions**
- **Paul Ekman**: Seven basic emotions with universal expressions
- **Cross-cultural**: Recognized across cultures
- **Examples**: Happiness, sadness, anger, fear, disgust, surprise, contempt

**Microexpressions**
- **Brief expressions**: Leak true emotions
- **Detection**: Trained observers can identify deception
- **Applications**: Security, therapy, research

**Body Language**

**Posture and Gesture**
- **Dominance**: Expansive postures signal power
- **Submission**: Contracted postures signal lower status
- **Cultural differences**: Gestures have different meanings
- **Emblems**: Gestures with specific meanings (thumbs up)

**Personal Space**
- **Proxemics**: Study of spatial behavior
- **Intimate distance**: 0-18 inches (close relationships)
- **Personal distance**: 18 inches-4 feet (friends)
- **Social distance**: 4-12 feet (acquaintances)
- **Public distance**: 12+ feet (formal situations)

**Eye Contact**
- **Attention**: Shows interest and engagement
- **Dominance**: Direct gaze can signal power
- **Attraction**: Mutual gaze increases liking
- **Cultural norms**: Rules vary by culture

### Accuracy in Person Perception

**When We're Accurate**
- **Observable traits**: Extraversion easier to judge than neuroticism
- **Familiar people**: Accuracy increases with acquaintance
- **Multiple observations**: More information improves accuracy
- **Motivated perceivers**: Accuracy goals improve performance

**When We're Inaccurate**
- **Projection**: Assume others are like us
- **Halo effect**: One trait influences all judgments
- **Confirmation bias**: Seek information that confirms impressions
- **Stereotyping**: Category-based rather than individual-based judgments

**Improving Accuracy**
- **Seek disconfirming information**: Look for evidence against initial impressions
- **Consider alternative explanations**: Think of other possible causes
- **Get to know individuals**: Move beyond category-based judgments
- **Be aware of biases**: Recognize tendency toward biased processing
- **Multiple perspectives**: Consider how others might view the person

### Social Cognition in Everyday Life

**Job Interviews**
- **First impressions**: Formed within minutes
- **Halo effect**: One positive trait influences entire evaluation
- **Confirmation bias**: Seek information that confirms initial impression
- **Similarity bias**: Prefer candidates similar to ourselves

**Educational Settings**
- **Teacher expectations**: Influence student performance
- **Stereotype threat**: Students worry about confirming negative stereotypes
- **Attribution patterns**: Success attributed to ability vs. effort
- **Feedback interpretation**: Same feedback interpreted differently based on expectations

**Healthcare**
- **Patient stereotypes**: Affect treatment decisions
- **Symptom interpretation**: Same symptoms interpreted differently for different groups
- **Communication styles**: Influenced by assumptions about patients
- **Treatment adherence**: Expectations affect patient compliance

**Legal System**
- **Defendant characteristics**: Appearance influences verdicts
- **Victim blaming**: Attributions about victim responsibility
- **Eyewitness testimony**: Influenced by schemas and expectations
- **Jury decision-making**: Biases in evidence evaluation

Understanding social cognition helps us recognize the biases and shortcuts in our thinking about others, leading to more accurate and fair judgments.`
        },
        {
          id: "attribution-exercise",
          title: "Attribution Analysis Exercise",
          description: "Practice identifying attribution patterns and biases in social situations",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "attribution-analysis",
            instructions: "Analyze the attribution patterns in each scenario using Kelley's covariation model and identify any attribution biases.",
            scenarios: [
              {
                id: "student-performance",
                situation: "Sarah gets an A on her psychology exam",
                information: {
                  consensus: "Most other students got B's or C's (Low consensus)",
                  distinctiveness: "Sarah gets A's in all her classes (Low distinctiveness)",
                  consistency: "Sarah always gets A's in psychology (High consistency)"
                },
                attribution: "Internal (Sarah is intelligent and hardworking)",
                biases: ["Could show self-serving bias if Sarah makes this attribution about herself"]
              },
              {
                id: "rude-behavior",
                situation: "John is rude to a waitress at a restaurant",
                information: {
                  consensus: "Other customers are polite to the waitress (Low consensus)",
                  distinctiveness: "John is rude to many service workers (Low distinctiveness)",
                  consistency: "John is usually rude to waitresses (High consistency)"
                },
                attribution: "Internal (John has a rude personality)",
                biases: ["Fundamental attribution error - might not consider that John had a bad day"]
              }
            ]
          }
        }
      ],
      quiz: {
        id: "social-cognition-quiz",
        title: "Social Cognition Quiz",
        description: "Test your understanding of attribution theory and person perception",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "The fundamental attribution error involves:",
            options: [
              "Overestimating situational factors when explaining others' behavior",
              "Overestimating dispositional factors when explaining others' behavior",
              "Underestimating our own role in social situations",
              "Making too many attributions about behavior"
            ],
            correctAnswer: 1,
            explanation: "The fundamental attribution error is the tendency to overestimate internal (dispositional) factors and underestimate external (situational) factors when explaining others' behavior."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "According to Kelley's covariation model, an internal attribution is most likely when:",
            options: [
              "Consensus is high, distinctiveness is high, consistency is high",
              "Consensus is low, distinctiveness is low, consistency is high",
              "Consensus is high, distinctiveness is low, consistency is low",
              "Consensus is low, distinctiveness is high, consistency is low"
            ],
            correctAnswer: 1,
            explanation: "Internal attributions are made when consensus is low (few others behave this way), distinctiveness is low (person behaves this way in many situations), and consistency is high (behavior is stable)."
          }
        ]
      }
    },
    {
      id: "applied-social-psychology",
      title: "Applied Social Psychology",
      description: "Applying social psychological principles to real-world issues and interventions",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "applied-intro",
          title: "Social Psychology in Practice",
          description: "How social psychological research applies to real-world problems and solutions",
          type: "video",
          duration: 30
        },
        {
          id: "real-world-applications",
          title: "Real-World Applications and Interventions",
          description: "Using social psychology to address practical problems in society",
          type: "text",
          duration: 50,
          textContent: `# Real-World Applications and Interventions

## Applied Social Psychology Overview

### What is Applied Social Psychology?

Applied social psychology uses social psychological theories, methods, and research findings to understand and solve real-world problems.

**Key Characteristics**
- **Problem-focused**: Addresses specific social issues
- **Intervention-oriented**: Develops solutions, not just understanding
- **Evidence-based**: Uses scientific research to guide practice
- **Multidisciplinary**: Collaborates with other fields

**Core Principles**
- **Theory to practice**: Apply theoretical knowledge to real problems
- **Research-based**: Use empirical evidence to guide interventions
- **Evaluation**: Assess effectiveness of interventions
- **Continuous improvement**: Refine approaches based on outcomes

### Health Behavior Change

**Health Psychology Applications**

**Smoking Cessation**

**Social Cognitive Theory Applications**
- **Self-efficacy**: Build confidence in ability to quit
- **Outcome expectations**: Emphasize benefits of quitting
- **Social support**: Use buddy systems and group programs
- **Modeling**: Show successful quitters as role models

**Theory of Planned Behavior**
- **Attitudes**: Change beliefs about smoking consequences
- **Subjective norms**: Address social pressure to smoke
- **Perceived behavioral control**: Increase sense of control over quitting
- **Implementation intentions**: Plan specific quit strategies

**Social Influence Techniques**
- **Commitment and consistency**: Public commitment to quit
- **Social proof**: Show that many people successfully quit
- **Authority**: Use medical expert recommendations
- **Reciprocity**: Frame quitting as gift to loved ones

**Exercise and Physical Activity**

**Social Support Interventions**
- **Workout partners**: Exercise with friends or family
- **Group fitness**: Join exercise classes or teams
- **Online communities**: Virtual support and accountability
- **Professional support**: Personal trainers and coaches

**Goal Setting and Self-Regulation**
- **SMART goals**: Specific, measurable, achievable, relevant, time-bound
- **Self-monitoring**: Track progress and behavior
- **Feedback**: Regular assessment of goal progress
- **Rewards**: Celebrate achievements and milestones

**Environmental Design**
- **Accessibility**: Make exercise convenient and available
- **Cues**: Environmental prompts for physical activity
- **Social norms**: Create cultures that value exercise
- **Policy changes**: Workplace wellness programs

### Educational Applications

**Stereotype Threat Reduction**

**Intervention Strategies**
- **Reframing**: Present tasks as learning rather than ability tests
- **Role models**: Provide examples of successful group members
- **Affirmation**: Self-affirmation exercises before tests
- **Growth mindset**: Emphasize that abilities can be developed

**Cooperative Learning**

**Jigsaw Classroom**
- **Structure**: Students depend on each other for learning
- **Benefits**: Reduces prejudice, improves achievement
- **Implementation**: Each student has unique information
- **Outcomes**: Increased liking across racial groups

**Contact Theory Applications**
- **Equal status**: Ensure students have similar roles
- **Common goals**: Work toward shared objectives
- **Cooperation**: Structure positive interdependence
- **Authority support**: Teacher endorsement of cooperation

**Motivation and Achievement**

**Self-Determination Theory**
- **Autonomy**: Give students choices and control
- **Competence**: Provide optimal challenges and feedback
- **Relatedness**: Create sense of belonging and connection
- **Intrinsic motivation**: Focus on learning rather than grades

**Attribution Training**
- **Effort attributions**: Teach that effort leads to success
- **Strategy focus**: Emphasize effective learning strategies
- **Incremental beliefs**: Abilities can be developed through practice
- **Resilience**: Failures are learning opportunities

### Workplace Applications

**Team Effectiveness**

**Team Building Interventions**
- **Goal clarity**: Establish clear, shared objectives
- **Role definition**: Clarify responsibilities and expectations
- **Communication training**: Improve interaction skills
- **Trust building**: Activities to increase interpersonal trust

**Diversity and Inclusion**

**Bias Reduction Training**
- **Awareness**: Recognize unconscious biases
- **Perspective-taking**: Understand others' experiences
- **Contact opportunities**: Increase positive intergroup interaction
- **Accountability**: Measure and track bias reduction efforts

**Inclusive Leadership**
- **Cultural competence**: Understanding diverse perspectives
- **Equitable practices**: Fair treatment and opportunities
- **Ally behavior**: Supporting underrepresented groups
- **Systemic change**: Addressing structural barriers

**Organizational Change**

**Change Management**
- **Communication**: Clear, consistent messaging about change
- **Participation**: Involve employees in change process
- **Support**: Provide resources and training
- **Resistance management**: Address concerns and objections

**Culture Change**
- **Values alignment**: Connect change to organizational values
- **Leadership modeling**: Leaders demonstrate desired behaviors
- **Social norms**: Create new behavioral expectations
- **Recognition**: Reward behaviors that support change

### Criminal Justice Applications

**Eyewitness Testimony**

**Research Findings**
- **Memory reconstruction**: Memories change over time
- **Leading questions**: Question wording affects responses
- **Confidence-accuracy**: Confidence doesn't predict accuracy
- **Cross-race effect**: Harder to identify other-race faces

**Interventions**
- **Cognitive interview**: Techniques to improve recall
- **Lineup procedures**: Double-blind, sequential lineups
- **Expert testimony**: Educate juries about memory limitations
- **Training**: Educate law enforcement about best practices

**Jury Decision-Making**

**Bias Reduction**
- **Jury selection**: Identify and address potential biases
- **Instructions**: Clear guidance about legal standards
- **Evidence presentation**: Reduce prejudicial information
- **Deliberation structure**: Procedures to improve decision quality

**Rehabilitation and Recidivism**

**Cognitive-Behavioral Interventions**
- **Thinking errors**: Address criminal thinking patterns
- **Social skills**: Improve interpersonal abilities
- **Anger management**: Control aggressive impulses
- **Substance abuse**: Address addiction issues

**Social Support and Reintegration**
- **Mentorship**: Pair with successful community members
- **Employment assistance**: Job training and placement
- **Housing support**: Stable living arrangements
- **Family reconnection**: Rebuild family relationships

### Environmental and Conservation Psychology

**Pro-Environmental Behavior**

**Attitude-Behavior Models**
- **Knowledge-attitude-behavior**: Education alone insufficient
- **Theory of planned behavior**: Address norms and control
- **Value-belief-norm**: Connect to personal values
- **Social cognitive theory**: Self-efficacy and outcome expectations

**Social Influence Techniques**
- **Social norms**: Show what others are doing
- **Commitment**: Get public pledges to act environmentally
- **Feedback**: Provide information about environmental impact
- **Social proof**: Highlight popular environmental behaviors

**Nudging and Choice Architecture**

**Behavioral Economics Applications**
- **Default options**: Make pro-environmental choice default
- **Framing**: Present information to encourage good choices
- **Salience**: Make environmental costs and benefits visible
- **Timing**: Present information when decisions are made

**Examples**
- **Energy conservation**: Home energy reports comparing to neighbors
- **Recycling**: Make recycling easier than throwing away
- **Transportation**: Default to public transit in trip planning apps
- **Water conservation**: Low-flow showerheads as standard

### Mental Health Applications

**Stigma Reduction**

**Contact Interventions**
- **Personal contact**: Interactions with people with mental illness
- **Education plus contact**: Combine information with interaction
- **Protest**: Challenge discriminatory representations
- **Media campaigns**: Positive portrayals of mental illness

**Social Support Interventions**

**Support Groups**
- **Peer support**: Connect people with similar experiences
- **Mutual aid**: Members help each other
- **Shared coping**: Learn strategies from others
- **Reduced isolation**: Combat loneliness and stigma

**Community Mental Health**
- **Social networks**: Strengthen community connections
- **Neighborhood cohesion**: Build sense of community
- **Collective efficacy**: Community ability to solve problems
- **Resource access**: Improve availability of mental health services

### Technology and Social Media

**Cyberbullying Prevention**

**Bystander Intervention**
- **Notice**: Recognize cyberbullying when it occurs
- **Interpret**: Understand that intervention is needed
- **Responsibility**: Feel obligated to act
- **Skills**: Know how to intervene effectively
- **Action**: Actually intervene to stop bullying

**Empathy Training**
- **Perspective-taking**: Understand victim's experience
- **Emotional awareness**: Recognize impact of cyberbullying
- **Moral development**: Connect to values about treating others
- **Upstander behavior**: Encourage supportive responses

**Digital Wellness**

**Social Media Use**
- **Mindful use**: Conscious decisions about social media engagement
- **Social comparison**: Awareness of comparison processes
- **FOMO reduction**: Address fear of missing out
- **Real-world connections**: Balance online and offline relationships

**Information Literacy**
- **Source evaluation**: Assess credibility of online information
- **Confirmation bias**: Recognize tendency to seek confirming information
- **Echo chambers**: Understand algorithm-driven content
- **Critical thinking**: Evaluate claims and evidence

### Evaluation and Research Methods

**Program Evaluation**

**Outcome Measurement**
- **Behavioral indicators**: Objective measures of behavior change
- **Attitude assessment**: Changes in beliefs and attitudes
- **Knowledge tests**: Understanding of key concepts
- **Long-term follow-up**: Sustained behavior change

**Research Designs**
- **Randomized controlled trials**: Gold standard for intervention testing
- **Quasi-experimental**: When randomization not possible
- **Case studies**: In-depth examination of specific interventions
- **Mixed methods**: Combine quantitative and qualitative approaches

**Implementation Science**

**Translating Research to Practice**
- **Efficacy vs. effectiveness**: Laboratory vs. real-world settings
- **Scalability**: Can interventions work on larger scale?
- **Sustainability**: Will effects continue without ongoing support?
- **Adaptation**: How to modify interventions for different contexts?

**Stakeholder Engagement**
- **Community involvement**: Include affected communities in design
- **Practitioner input**: Work with those who will implement
- **Policy maker engagement**: Consider regulatory and funding constraints
- **Cultural adaptation**: Modify for different cultural contexts

### Future Directions

**Emerging Technologies**
- **Virtual reality**: Immersive experiences for empathy and training
- **Mobile apps**: Deliver interventions through smartphones
- **Artificial intelligence**: Personalized intervention recommendations
- **Wearable devices**: Real-time monitoring and feedback

**Global Challenges**
- **Climate change**: Behavior change for environmental protection
- **Health disparities**: Address unequal health outcomes
- **Social inequality**: Reduce prejudice and discrimination
- **Political polarization**: Bridge divides and improve dialogue

**Methodological Advances**
- **Big data**: Analyze large-scale behavioral patterns
- **Longitudinal studies**: Track changes over longer periods
- **Cross-cultural research**: Test universality of principles
- **Intervention combinations**: Optimize multiple intervention components

Applied social psychology demonstrates the practical value of psychological research in addressing society's most pressing challenges.`
        },
        {
          id: "intervention-design",
          title: "Social Intervention Design",
          description: "Design evidence-based interventions using social psychological principles",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "intervention-design",
            instructions: "Design a social psychology-based intervention for the given problem using theoretical principles and research evidence.",
            problem: {
              title: "Reducing Workplace Bias in Hiring",
              context: "A technology company has noticed that their hiring process may be biased against women and underrepresented minorities, despite efforts to recruit diverse candidates.",
              goals: ["Reduce unconscious bias in hiring decisions", "Increase diversity of candidates interviewed", "Improve fairness of evaluation process"],
              constraints: ["Limited budget for training", "Resistance from some hiring managers", "Need to maintain hiring quality", "Time pressure in hiring process"]
            },
            designSteps: [
              {
                step: "Identify Target Behaviors",
                prompt: "What specific behaviors need to change in the hiring process?",
                examples: ["Resume screening decisions", "Interview questions and evaluation", "Final hiring recommendations"]
              },
              {
                step: "Apply Theoretical Framework",
                prompt: "Which social psychology theories and principles are relevant?",
                options: ["Implicit bias theory", "Contact hypothesis", "System justification", "Social identity theory"]
              },
              {
                step: "Design Intervention Components",
                prompt: "What specific intervention techniques will you use?",
                techniques: ["Blind resume review", "Structured interviews", "Bias awareness training", "Diverse hiring panels"]
              },
              {
                step: "Implementation Plan",
                prompt: "How will you implement and evaluate the intervention?",
                considerations: ["Training delivery", "Resistance management", "Outcome measurement", "Continuous improvement"]
              }
            ]
          }
        }
      ],
      quiz: {
        id: "applied-social-psychology-quiz",
        title: "Applied Social Psychology Quiz",
        description: "Test your understanding of how social psychology applies to real-world problems",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "The jigsaw classroom technique is designed to:",
            options: [
              "Improve individual academic performance only",
              "Reduce intergroup prejudice through cooperative learning",
              "Increase competition between students",
              "Simplify complex academic material"
            ],
            correctAnswer: 1,
            explanation: "The jigsaw classroom creates positive interdependence between students from different groups, reducing prejudice through cooperative contact under optimal conditions."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "According to applied social psychology research, the most effective way to change health behaviors is to:",
            options: [
              "Provide information about health risks only",
              "Use fear appeals exclusively",
              "Combine multiple theoretical approaches and intervention techniques",
              "Focus only on individual willpower"
            ],
            correctAnswer: 2,
            explanation: "Research shows that combining multiple approaches (education, social support, environmental changes, etc.) is more effective than single-component interventions."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "In environmental psychology, 'nudging' refers to:",
            options: [
              "Forcing people to make pro-environmental choices",
              "Designing choice architecture to encourage better decisions",
              "Providing financial incentives for environmental behavior",
              "Eliminating all non-environmental options"
            ],
            correctAnswer: 1,
            explanation: "Nudging involves designing the choice environment to make pro-environmental options more likely while preserving freedom of choice."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "social-psychology-experiment",
      title: "Social Psychology Research Project",
      description: "Design and conduct a small-scale study examining a social psychological phenomenon",
      type: "project",
      dueWeeks: 12,
      maxScore: 100,
      instructions: "Choose a social psychological concept, design an ethical study to investigate it, collect and analyze data, and present your findings with proper interpretation and discussion of limitations.",
      submissionFormat: "pdf",
      resources: [
        "Research ethics guidelines",
        "Social psychology experiment ideas",
        "Data analysis software tutorials",
        "APA style guide for psychology"
      ]
    }
  ],

  finalExam: {
    id: "social-psychology-final-exam",
    title: "Social Psychology Final Examination",
    description: "Comprehensive examination covering major topics in social psychology including influence, attitudes, groups, and behavior",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "What percentage of participants in Milgram's original obedience study delivered the maximum shock?",
        options: [
          "25%",
          "45%",
          "65%",
          "85%"
        ],
        correctAnswer: 2,
        explanation: "In Milgram's original study, 65% of participants delivered the maximum 450-volt shock, demonstrating the powerful influence of authority on obedient behavior."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "Social loafing refers to:",
        options: [
          "Working harder in groups than alone",
          "Working less hard in groups than alone",
          "Avoiding social situations",
          "Following group leaders blindly"
        ],
        correctAnswer: 1,
        explanation: "Social loafing is the tendency for individuals to exert less effort when working in a group than when working alone, often due to diffusion of responsibility."
      }
    ]
  },

  quizzes: []
};